import {
  WORKSPACE_SCHEMA_VERSION,
  type WorkspaceCardLayoutItem,
  type WorkspaceCardLayouts,
  type WorkspaceLayoutInput,
  type WorkspaceLayoutPreference,
} from '../../services/api/workspace';

/** Cards that ship with the workspace and are declared in the registry below. */
export type WorkspaceStaticCardId =
  | 'radar'
  | 'collaboration'
  | 'inventory-health'
  | 'recent-sales-orders'
  | 'recent-purchase-orders'
  | 'purchase-orders-at-risk'
  | 'shipment-flow'
  | 'warehouse-capacity'
  | 'warehouse-task-aging';

/**
 * An analytics chart pinned to the workspace, keyed by its saved visual id.
 *
 * These are user-chosen rather than declared, so they have no registry entry —
 * `chartCardDefinition()` synthesises one. The `chart:` prefix keeps them
 * distinguishable from static ids and satisfies the server's card-id pattern
 * (`/^[a-z0-9][a-z0-9:_-]{0,127}$/i`), so no backend change is needed.
 */
export type WorkspaceChartCardId = `chart:${string}`;

export type WorkspaceCardId = WorkspaceStaticCardId | WorkspaceChartCardId;

export const isChartCardId = (id: string): id is WorkspaceChartCardId => id.startsWith('chart:');

/** The saved visual id behind a chart card. */
export const visualIdFromCardId = (id: WorkspaceChartCardId): string => id.slice('chart:'.length);

export const chartCardIdFor = (visualId: string): WorkspaceChartCardId => `chart:${visualId}`;

/**
 * Chart cards are not in the static height table. They default to the same
 * footprint as the taller built-in cards — a chart squeezed into a short card
 * is unreadable, which defeats the point of pinning it.
 */
const CHART_CARD_HEIGHT = 10;
const CHART_CARD_SPAN = 3;

/** Registry entry for a chart card, which has none of its own. */
export const chartCardDefinition = (
  id: WorkspaceChartCardId,
  title = 'Chart'
): WorkspaceCardDefinition => ({
  id,
  title,
  module: 'Analytics',
  moduleHref: '/analytics',
  permissions: ['analytics:dashboard:read'],
  defaultSpan: CHART_CARD_SPAN,
  anchorLabel: title,
});

export type WorkspaceCardSpan = 2 | 3 | 4 | 6;
export type WorkspaceBreakpoint = keyof WorkspaceCardLayouts;

export const WORKSPACE_GRID_COLUMNS: Record<WorkspaceBreakpoint, number> = {
  lg: 6,
  md: 2,
  sm: 1,
};

export interface WorkspaceCardDefinition {
  id: WorkspaceCardId;
  title: string;
  module: string;
  /** Where the card's module label navigates to. */
  moduleHref: string;
  requiredPlugin?: string;
  permissions: string[];
  defaultSpan: WorkspaceCardSpan;
  anchorLabel: string;
}

export const WORKSPACE_CARD_REGISTRY: readonly WorkspaceCardDefinition[] = [
  {
    id: 'radar',
    title: 'Radar',
    module: 'Operations',
    moduleHref: '/shipments/radar',
    permissions: [],
    defaultSpan: 4,
    anchorLabel: 'Radar',
  },
  {
    id: 'collaboration',
    title: 'My Tasks',
    module: 'Collaboration',
    moduleHref: '/tasks',
    requiredPlugin: 'tasks',
    permissions: ['tasks:task:read'],
    // 2 so it lands beside the 4-wide radar, filling the first row under the
    // insights card exactly — the two cards a shift starts from.
    defaultSpan: 2,
    anchorLabel: 'Tasks',
  },
  {
    id: 'inventory-health',
    title: 'Inventory Health',
    module: 'Inventory',
    moduleHref: '/inventory/items',
    requiredPlugin: 'inventory',
    permissions: ['inventory:item:read'],
    defaultSpan: 2,
    anchorLabel: 'Inventory',
  },
  {
    id: 'recent-sales-orders',
    title: 'Recent Sales Orders',
    module: 'Orders',
    moduleHref: '/orders/sales',
    requiredPlugin: 'orders',
    permissions: ['order:sales:read'],
    defaultSpan: 3,
    anchorLabel: 'Orders',
  },
  {
    id: 'recent-purchase-orders',
    title: 'Recent Purchase Orders',
    module: 'Orders',
    moduleHref: '/orders/purchase',
    requiredPlugin: 'orders',
    permissions: ['order:purchase:read'],
    defaultSpan: 3,
    anchorLabel: 'Purchasing',
  },
  {
    id: 'purchase-orders-at-risk',
    title: 'Purchase Orders at Risk',
    module: 'Orders',
    moduleHref: '/orders/purchase',
    requiredPlugin: 'orders',
    permissions: ['order:purchase:read'],
    defaultSpan: 3,
    anchorLabel: 'PO risk',
  },
  {
    id: 'shipment-flow',
    title: 'Shipment Flow',
    module: 'Shipments',
    moduleHref: '/shipments',
    requiredPlugin: 'shipments',
    permissions: ['shipment:shipment:read'],
    defaultSpan: 3,
    anchorLabel: 'Shipments',
  },
  {
    id: 'warehouse-capacity',
    title: 'Warehouse Capacity',
    module: 'Warehouse',
    moduleHref: '/warehouse/warehouses',
    requiredPlugin: 'warehouse',
    permissions: ['warehouse:warehouse:read'],
    defaultSpan: 3,
    anchorLabel: 'Warehouse',
  },
  {
    id: 'warehouse-task-aging',
    title: 'Warehouse Task Aging',
    module: 'Warehouse',
    moduleHref: '/warehouse/tasks',
    requiredPlugin: 'warehouse',
    permissions: ['warehouse:task:read'],
    defaultSpan: 3,
    anchorLabel: 'Task aging',
  },
] as const;

export interface WorkspaceLayoutState {
  orderedCardIds: WorkspaceCardId[];
  pinnedCardIds: WorkspaceCardId[];
  cardLayouts: WorkspaceCardLayouts;
}

const DEFAULT_HEIGHTS: Record<WorkspaceStaticCardId, number> = {
  radar: 10,
  collaboration: 10,
  'inventory-health': 10,
  'recent-sales-orders': 9,
  'recent-purchase-orders': 9,
  'purchase-orders-at-risk': 9,
  'shipment-flow': 8,
  'warehouse-capacity': 8,
  'warehouse-task-aging': 8,
};


function defaultHeight(cardId: WorkspaceCardId): number {
  return isChartCardId(cardId)
    ? CHART_CARD_HEIGHT
    : DEFAULT_HEIGHTS[cardId as WorkspaceStaticCardId] ?? CHART_CARD_HEIGHT;
}

function defaultWidth(cardId: WorkspaceCardId, breakpoint: WorkspaceBreakpoint): number {
  if (breakpoint === 'sm') return 1;
  if (breakpoint === 'md') return cardId === 'radar' ? 2 : 1;
  if (isChartCardId(cardId)) return CHART_CARD_SPAN;
  return WORKSPACE_CARD_REGISTRY.find((definition) => definition.id === cardId)?.defaultSpan ?? 2;
}

function packWorkspaceLayout(
  orderedCardIds: WorkspaceCardId[],
  breakpoint: WorkspaceBreakpoint,
  source?: WorkspaceCardLayoutItem[]
): WorkspaceCardLayoutItem[] {
  const columns = WORKSPACE_GRID_COLUMNS[breakpoint];
  const sourceById = new Map(source?.map((item) => [item.i, item]));
  const packed: WorkspaceCardLayoutItem[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  for (const id of orderedCardIds) {
    const sourceItem = sourceById.get(id);
    const width = Math.max(
      1,
      Math.min(columns, Math.round(sourceItem?.w ?? defaultWidth(id, breakpoint)))
    );
    const height = Math.max(4, Math.min(20, Math.round(sourceItem?.h ?? defaultHeight(id))));

    if (x + width > columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }

    packed.push({ i: id, x, y, w: width, h: height });
    x += width;
    rowHeight = Math.max(rowHeight, height);
    if (x === columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }
  }

  return packed;
}

export function createDefaultWorkspaceCardLayouts(
  orderedCardIds: WorkspaceCardId[]
): WorkspaceCardLayouts {
  return {
    lg: packWorkspaceLayout(orderedCardIds, 'lg'),
    md: packWorkspaceLayout(orderedCardIds, 'md'),
    sm: packWorkspaceLayout(orderedCardIds, 'sm'),
  };
}

function reconcileBreakpointLayout(
  savedItems: WorkspaceCardLayoutItem[] | undefined,
  availableCardIds: WorkspaceCardId[],
  breakpoint: WorkspaceBreakpoint
): WorkspaceCardLayoutItem[] {
  if (!savedItems?.length) return packWorkspaceLayout(availableCardIds, breakpoint);

  const available = new Set(availableCardIds);
  const columns = WORKSPACE_GRID_COLUMNS[breakpoint];
  const seen = new Set<WorkspaceCardId>();
  const retained: WorkspaceCardLayoutItem[] = [];

  for (const item of savedItems) {
    const id = item.i as WorkspaceCardId;
    if (!available.has(id) || seen.has(id)) continue;
    const width = Math.max(1, Math.min(columns, Math.round(item.w)));
    retained.push({
      i: id,
      x: Math.max(0, Math.min(columns - width, Math.round(item.x))),
      y: Math.max(0, Math.round(item.y)),
      w: width,
      h: Math.max(4, Math.min(20, Math.round(item.h))),
    });
    seen.add(id);
  }

  const missing = availableCardIds.filter((id) => !seen.has(id));
  if (!missing.length) return retained;
  const yOffset = retained.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0);
  return [
    ...retained,
    ...packWorkspaceLayout(missing, breakpoint).map((item) => ({
      ...item,
      y: item.y + yOffset,
    })),
  ];
}

export function reconcileWorkspaceLayout(
  saved: Pick<
    WorkspaceLayoutPreference,
    'orderedCardIds' | 'pinnedCardIds' | 'cardLayouts'
  > | null | undefined,
  availableCardIds: WorkspaceCardId[]
): WorkspaceLayoutState {
  const available = new Set(availableCardIds);
  const seen = new Set<WorkspaceCardId>();
  const ordered: WorkspaceCardId[] = [];

  for (const rawId of saved?.orderedCardIds ?? []) {
    const id = rawId as WorkspaceCardId;
    if (!available.has(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }

  for (const id of availableCardIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }

  const pinnedSet = new Set<WorkspaceCardId>();
  for (const rawId of saved?.pinnedCardIds ?? []) {
    const id = rawId as WorkspaceCardId;
    if (seen.has(id)) pinnedSet.add(id);
  }

  const orderedCardIds = [
    ...ordered.filter((id) => pinnedSet.has(id)),
    ...ordered.filter((id) => !pinnedSet.has(id)),
  ];

  return {
    orderedCardIds,
    pinnedCardIds: ordered.filter((id) => pinnedSet.has(id)),
    cardLayouts: {
      lg: reconcileBreakpointLayout(saved?.cardLayouts?.lg, orderedCardIds, 'lg'),
      md: reconcileBreakpointLayout(saved?.cardLayouts?.md, orderedCardIds, 'md'),
      sm: reconcileBreakpointLayout(saved?.cardLayouts?.sm, orderedCardIds, 'sm'),
    },
  };
}

export function serializeWorkspaceLayout(state: WorkspaceLayoutState): WorkspaceLayoutInput {
  return {
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    orderedCardIds: state.orderedCardIds,
    pinnedCardIds: state.pinnedCardIds,
    cardLayouts: state.cardLayouts,
  };
}

export function workspaceLayoutsEqual(
  left: WorkspaceLayoutState,
  right: WorkspaceLayoutState
): boolean {
  return (
    left.orderedCardIds.join('|') === right.orderedCardIds.join('|') &&
    left.pinnedCardIds.join('|') === right.pinnedCardIds.join('|') &&
    JSON.stringify(left.cardLayouts) === JSON.stringify(right.cardLayouts)
  );
}

function reflowWorkspaceLayouts(
  cardLayouts: WorkspaceCardLayouts,
  orderedCardIds: WorkspaceCardId[]
): WorkspaceCardLayouts {
  return {
    lg: packWorkspaceLayout(orderedCardIds, 'lg', cardLayouts.lg),
    md: packWorkspaceLayout(orderedCardIds, 'md', cardLayouts.md),
    sm: packWorkspaceLayout(orderedCardIds, 'sm', cardLayouts.sm),
  };
}

export function moveWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId,
  targetIndex: number
): WorkspaceLayoutState {
  const fromIndex = state.orderedCardIds.indexOf(cardId);
  if (fromIndex < 0) return state;

  const next = [...state.orderedCardIds];
  next.splice(fromIndex, 1);
  next.splice(Math.max(0, Math.min(targetIndex, next.length)), 0, cardId);

  return {
    orderedCardIds: next,
    pinnedCardIds: state.pinnedCardIds.filter((id) => next.includes(id)),
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, next),
  };
}

/**
 * Adds a card to the FRONT of the board (after any pinned cards).
 *
 * Appending would drop a just-added card at the bottom of a long grid, where
 * the user cannot see the result of their own action.
 */
export function addWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId
): WorkspaceLayoutState {
  if (state.orderedCardIds.includes(cardId)) return state;
  const pinned = state.orderedCardIds.filter((id) => state.pinnedCardIds.includes(id));
  const rest = state.orderedCardIds.filter((id) => !state.pinnedCardIds.includes(id));
  const orderedCardIds = [...pinned, cardId, ...rest];
  return {
    orderedCardIds,
    pinnedCardIds: state.pinnedCardIds,
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, orderedCardIds),
  };
}

/** Drops a card from the order, the pins, and every breakpoint's layout. */
export function removeWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId
): WorkspaceLayoutState {
  if (!state.orderedCardIds.includes(cardId)) return state;
  const orderedCardIds = state.orderedCardIds.filter((id) => id !== cardId);
  const drop = (items: WorkspaceCardLayoutItem[]) => items.filter((item) => item.i !== cardId);
  return {
    orderedCardIds,
    pinnedCardIds: state.pinnedCardIds.filter((id) => id !== cardId),
    cardLayouts: reflowWorkspaceLayouts(
      { lg: drop(state.cardLayouts.lg), md: drop(state.cardLayouts.md), sm: drop(state.cardLayouts.sm) },
      orderedCardIds
    ),
  };
}

export function toggleWorkspaceCardPin(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId
): WorkspaceLayoutState {
  const isPinned = state.pinnedCardIds.includes(cardId);
  const pinnedCardIds = isPinned
    ? state.pinnedCardIds.filter((id) => id !== cardId)
    : [...state.pinnedCardIds, cardId];
  const pinnedSet = new Set(pinnedCardIds);
  const orderedCardIds = [
    ...state.orderedCardIds.filter((id) => pinnedSet.has(id)),
    ...state.orderedCardIds.filter((id) => !pinnedSet.has(id)),
  ];
  return {
    orderedCardIds,
    pinnedCardIds: orderedCardIds.filter((id) => pinnedSet.has(id)),
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, orderedCardIds),
  };
}

export function applyWorkspaceGridLayouts(
  state: WorkspaceLayoutState,
  layouts: Partial<Record<WorkspaceBreakpoint, readonly WorkspaceCardLayoutItem[]>>,
  activeBreakpoint: WorkspaceBreakpoint
): WorkspaceLayoutState {
  const nextLayouts = {
    lg: reconcileBreakpointLayout(
      layouts.lg ? [...layouts.lg] : state.cardLayouts.lg,
      state.orderedCardIds,
      'lg'
    ),
    md: reconcileBreakpointLayout(
      layouts.md ? [...layouts.md] : state.cardLayouts.md,
      state.orderedCardIds,
      'md'
    ),
    sm: reconcileBreakpointLayout(
      layouts.sm ? [...layouts.sm] : state.cardLayouts.sm,
      state.orderedCardIds,
      'sm'
    ),
  };
  const orderedCardIds = [...nextLayouts[activeBreakpoint]]
    .sort((left, right) => left.y - right.y || left.x - right.x)
    .map((item) => item.i as WorkspaceCardId);

  return {
    orderedCardIds,
    pinnedCardIds: state.pinnedCardIds.filter((id) => orderedCardIds.includes(id)),
    cardLayouts: nextLayouts,
  };
}
